export const UserRoles = {
  Collector: 'CHARITY_VOLUNTEER',
  Coordinator: 'CHARITY_COORDINATOR',
  LegalRepresentative: 'CHARITY_LEGAL_REPRESENTATIVE',
} as const;

export type UserRole = (typeof UserRoles)[keyof typeof UserRoles];

export const CharityVettingStatus = {
  Vetted: 'VETTED',
  VettingInProgress: 'VETTING_IN_PROGRESS',
  NotVetted: 'NOT_VETTED',
} as const;

export type CharityVettingStatusType =
  (typeof CharityVettingStatus)[keyof typeof CharityVettingStatus];

export type User = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  createdAt: string;
  isEmailVerified: boolean;
  welcomeEmailSentAt?: string;
};

export type ListUsersRequest = Record<string, never>;

export type ListUsersResponse = User[];

export type CreateUserRequest = {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
};

export type UpdateUserRequest = {
  userId: string;
  firstName: string;
  lastName: string;
  role: UserRole;
};

export type RemoveUserRequest = {
  userId: string;
};

export type ResendWelcomeEmailRequest = {
  userId: string;
};

export type OutgoingLegalRepRole = Extract<
  UserRole,
  typeof UserRoles.Coordinator | typeof UserRoles.Collector
>;

export const isOutgoingLegalRepRole = (role: UserRole): role is OutgoingLegalRepRole =>
  role === UserRoles.Coordinator || role === UserRoles.Collector;

export type TransferLegalRepRequest = {
  newLegalRepUserId: string;
  outgoingRole: OutgoingLegalRepRole;
};

export type TransferredMember = Omit<User, 'createdAt' | 'isEmailVerified' | 'welcomeEmailSentAt'>;

export type TransferLegalRepResponse = {
  newLegalRep: TransferredMember;
  previousLegalRep: TransferredMember;
};

export type CurrentUser = {
  charityId: string;
  charityName: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
  charityVettingStatus: CharityVettingStatusType;
  vettingSubmissionDate: string;
  notVettedDate: string;
  notVettedReason: string;
  admin?: boolean;
};

export type CompleteAccountRequest = Pick<User, 'firstName' | 'lastName'> & { password: string };
